// apps/admin/src/components/composer/properties-panel.tsx
//
// P5 UI Polish W5 T7 — Single-zone editor in PropertiesPanel (spec §2.3).
// P5 UI Polish W5 T8 — Multi-zone Aligner/Distribuer toolbar (spec §2.4).
// P5 UI Polish W6 T8 — Content-binding editor below X/Y/W/H (spec §8).
//
// When exactly one zone is selected, renders 4 number inputs (X, Y, W, H)
// bound 2-way to the zone state via the zustand+zundo store (patchZone).
// Apply on blur or Enter; Escape reverts. Out-of-range values clamp to
// canvas bounds with an inline toast. Content-type badge is read-only.
// Below the inputs, a ZoneBindingPicker lets the operator declare what
// the zone displays (Image / Video / Web page / RSS / Nested composer /
// Empty). Binding is local-only until W7 adds the server endpoint.
//
// When 2+ zones are selected, renders an 8-button toolbar:
// 6 alignment buttons (left / h-center / right / top / v-center / bottom)
// and 2 distribute buttons (h / v, enabled only at 3+ zones). Each click
// dispatches a single setZones call so zundo captures one undo step.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProgramSummary, Zone } from '@displaycms/contracts';
import { useComposerHistory } from '@/hooks/use-composer-history';
import { toast } from '@/lib/toast';
import { Icon } from '@/components/icon';
import {
  ZoneBindingPicker,
  type ZoneBindingValue,
} from '@/components/composer/zone-binding-picker';
import { ZonePlaylistEditor } from '@/components/composer/zone-playlist-editor';
import {
  alignLeft,
  alignHCenter,
  alignRight,
  alignTop,
  alignVCenter,
  alignBottom,
  distributeH,
  distributeV,
} from '@/lib/zone-alignment';

export interface PropertiesPanelProps {
  selectedZoneIds: ReadonlyArray<string>;
  canvasWidth?: number;
  canvasHeight?: number;
  /** W8 T2 — the composer whose program item is being edited. Required for binding persistence. */
  composerId?: string | undefined;
  /** W8 T2 — called after a binding is successfully saved so the parent can refetch. */
  onBindingSaved?: (() => void | Promise<void>) | undefined;
  /** Feature #8 — programs list for showing the existing zone playlist. */
  programs?: ProgramSummary[] | undefined;
}

// ---------------------------------------------------------------------------
// SingleZoneEditor — one field group per coordinate/dimension
// ---------------------------------------------------------------------------

interface FieldInputProps {
  field: 'x' | 'y' | 'w' | 'h';
  initialValue: number;
  label: string;
  onCommit: (field: 'x' | 'y' | 'w' | 'h', value: number) => void;
  onRevert: (field: 'x' | 'y' | 'w' | 'h') => void;
}

function FieldInput({ field, initialValue, label, onCommit, onRevert }: FieldInputProps): JSX.Element {
  const [draft, setDraft] = useState(String(initialValue));
  const skipBlurRef = useRef(false);

  // Sync draft when the store value changes externally (e.g. undo/redo).
  useEffect(() => {
    setDraft(String(initialValue));
  }, [initialValue]);

  function handleBlur(): void {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      // Revert draft to the current store value.
      setDraft(String(initialValue));
      onRevert(field);
      return;
    }
    const parsed = parseInt(draft, 10);
    if (!isNaN(parsed)) {
      onCommit(field, parsed);
    } else {
      setDraft(String(initialValue));
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      skipBlurRef.current = true;
      e.currentTarget.blur();
    }
  }

  return (
    <label className="flex flex-col gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type="number"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="w-full rounded border px-2 py-1 text-sm"
        data-testid={`single-zone-${field}-input`}
      />
    </label>
  );
}

interface SingleZoneEditorProps {
  zone: Zone;
  canvasWidth: number;
  canvasHeight: number;
  patchZone: (id: string, patch: Partial<Zone>) => void;
  /** W8 T2 — for binding persistence. */
  composerId?: string | undefined;
  onBindingSaved?: (() => void | Promise<void>) | undefined;
  /** Feature #8 — programs list for the playlist editor. */
  programs?: ProgramSummary[] | undefined;
}

function SingleZoneEditor({ zone, canvasWidth, canvasHeight, patchZone, composerId, onBindingSaved, programs }: SingleZoneEditorProps): JSX.Element {
  const { t } = useTranslation('composers');

  // Local-only binding state (mirrors what the server has for the zone).
  const [binding, setBinding] = useState<ZoneBindingValue | null>(null);

  // Count items in this zone to decide whether to show the playlist editor.
  const zoneItemCount = programs
    ? programs.flatMap((p) => p.items).filter((it) => it.zone_id === zone.id).length
    : 0;

  // Feature #8: show playlist editor when zone has more than 1 item.
  const showPlaylistEditor = composerId !== undefined && zoneItemCount > 1;

  function clampAndPatch(field: 'x' | 'y' | 'w' | 'h', raw: number): void {
    let clamped = raw;
    if (field === 'x') {
      clamped = Math.max(0, Math.min(raw, canvasWidth - zone.w));
    } else if (field === 'y') {
      clamped = Math.max(0, Math.min(raw, canvasHeight - zone.h));
    } else if (field === 'w') {
      clamped = Math.max(1, Math.min(raw, canvasWidth - zone.x));
    } else if (field === 'h') {
      clamped = Math.max(1, Math.min(raw, canvasHeight - zone.y));
    }
    if (clamped !== raw) {
      toast.error(t('properties_clamp_error'));
    }
    patchZone(zone.id, { [field]: clamped });
  }

  const LABELS: Record<'x' | 'y' | 'w' | 'h', string> = {
    x: t('properties_x_label'),
    y: t('properties_y_label'),
    w: t('properties_w_label'),
    h: t('properties_h_label'),
  };

  return (
    <div className="space-y-2" data-testid="single-zone-editor">
      <div className="grid grid-cols-2 gap-2">
        {(['x', 'y', 'w', 'h'] as const).map((field) => (
          <FieldInput
            key={field}
            field={field}
            initialValue={zone[field]}
            label={LABELS[field]}
            onCommit={clampAndPatch}
            onRevert={() => {
              // Revert is handled in FieldInput itself via skipBlurRef;
              // no store action needed.
            }}
          />
        ))}
      </div>
      <div className="text-xs">
        <span className="text-muted-foreground">{t('properties_content_type_label')}: </span>
        <span className="rounded bg-muted px-2 py-0.5" data-testid="single-zone-kind-badge">
          {zone.kind ?? '—'}
        </span>
      </div>
      {showPlaylistEditor && composerId !== undefined && programs !== undefined ? (
        <ZonePlaylistEditor
          composerId={composerId}
          zoneId={zone.id}
          programs={programs}
          onPlaylistSaved={onBindingSaved ?? (() => { /* no-op if not provided */ })}
        />
      ) : (
        <ZoneBindingPicker
          zoneId={zone.id}
          currentBinding={binding}
          composerId={composerId}
          onBindingSaved={onBindingSaved}
          onChange={setBinding}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MultiZoneAlignToolbar — 6 align + 2 distribute buttons (W5 T8)
// ---------------------------------------------------------------------------

interface MultiZoneAlignToolbarProps {
  selectedZoneIds: ReadonlyArray<string>;
  allZones: Zone[];
  setZones: (zones: Zone[]) => void;
}

function MultiZoneAlignToolbar({
  selectedZoneIds,
  allZones,
  setZones,
}: MultiZoneAlignToolbarProps): JSX.Element {
  const { t } = useTranslation('composers');
  const canDistribute = selectedZoneIds.length >= 3;

  function applyAlignment(fn: (zones: Zone[]) => Zone[]): void {
    const targets = allZones.filter((z) => selectedZoneIds.includes(z.id));
    const transformed = fn(targets);
    const updates = Object.fromEntries(transformed.map((z) => [z.id, z]));
    // Single setZones call so zundo captures one undo step regardless of
    // how many zones were moved.
    setZones(allZones.map((z) => updates[z.id] ?? z));
  }

  const btnClass =
    'flex items-center justify-center rounded border p-1 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="border-t pt-3">
      <h4 className="mb-2 text-xs font-semibold text-muted-foreground">
        {t('properties_align_section_title')}
      </h4>
      <div className="flex flex-wrap gap-1" data-testid="multi-zone-align-toolbar">
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment(alignLeft)}
          aria-label={t('align_left_aria')}
          data-testid="align-left"
        >
          <Icon name="align-start-horizontal" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment((z) => alignHCenter(z, 0))}
          aria-label={t('align_h_center_aria')}
          data-testid="align-h-center"
        >
          <Icon name="align-center-horizontal" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment(alignRight)}
          aria-label={t('align_right_aria')}
          data-testid="align-right"
        >
          <Icon name="align-end-horizontal" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment(alignTop)}
          aria-label={t('align_top_aria')}
          data-testid="align-top"
        >
          <Icon name="align-start-vertical" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment((z) => alignVCenter(z, 0))}
          aria-label={t('align_v_center_aria')}
          data-testid="align-v-center"
        >
          <Icon name="align-center-vertical" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment(alignBottom)}
          aria-label={t('align_bottom_aria')}
          data-testid="align-bottom"
        >
          <Icon name="align-end-vertical" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment(distributeH)}
          aria-label={t('distribute_h_aria')}
          disabled={!canDistribute}
          data-testid="distribute-h"
        >
          <Icon name="align-horizontal-distribute-center" size={14} />
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => applyAlignment(distributeV)}
          aria-label={t('distribute_v_aria')}
          disabled={!canDistribute}
          data-testid="distribute-v"
        >
          <Icon name="align-vertical-distribute-center" size={14} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PropertiesPanel (public)
// ---------------------------------------------------------------------------

export function PropertiesPanel({
  selectedZoneIds,
  canvasWidth = 1920,
  canvasHeight = 1080,
  composerId,
  onBindingSaved,
  programs,
}: PropertiesPanelProps): JSX.Element {
  const { t } = useTranslation('composers');
  const zones = useComposerHistory((s) => s.zones);
  const patchZone = useComposerHistory((s) => s.patchZone);
  const setZones = useComposerHistory((s) => s.setZones);

  const selectedZone: Zone | null =
    selectedZoneIds.length === 1
      ? (zones.find((z) => z.id === selectedZoneIds[0]) ?? null)
      : null;

  return (
    <aside
      data-testid="properties-panel"
      className="w-[280px] border-l border-border bg-background p-4"
    >
      {selectedZoneIds.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t('properties_empty', { defaultValue: 'Aucune zone sélectionnée.' })}
        </p>
      )}
      {selectedZoneIds.length === 1 && selectedZone !== null && (
        <SingleZoneEditor
          zone={selectedZone}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          patchZone={patchZone}
          composerId={composerId}
          onBindingSaved={onBindingSaved}
          programs={programs}
        />
      )}
      {selectedZoneIds.length === 1 && selectedZone === null && (
        <p className="text-sm text-muted-foreground">
          {t('properties_single_zone', { defaultValue: 'Une zone sélectionnée.' })}
        </p>
      )}
      {selectedZoneIds.length >= 2 && (
        <MultiZoneAlignToolbar
          selectedZoneIds={selectedZoneIds}
          allZones={zones}
          setZones={setZones}
        />
      )}
    </aside>
  );
}
